// ======================================================================
// ImsPage.utils.js
// Pure utility functions for IMS Reports
// ======================================================================

import { sortPeriodsChronologically } from "./ImsPeriodUtils";

// ======================================================================
// STILI PER REACT-SELECT (dropdown)
// ======================================================================
export const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  container: (base) => ({ ...base, fontSize: "0.875rem", width: "100%" }),
  containerTrend: (base) => ({ ...base, fontSize: "0.875rem", padding: 0, margin: 0 }),
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    fontSize: "0.875rem",
    borderColor: state.isFocused ? "#80bdff" : "#e9ecef",
    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : "none",
    "&:hover": { borderColor: "#80bdff" },
  }),
  option: (base) => ({
    ...base,
    fontSize: "0.875rem",
    padding: "8px 12px",
  }),
  multiValue: (base) => ({ ...base, backgroundColor: "#e9ecef" }),
  multiValueLabel: (base) => ({ ...base, fontSize: "0.75rem" }),
  dropdownIndicator: (provided) => ({
    ...provided,
    minHeight: "1px",
    paddingTop: "0",
    paddingBottom: "0",
    color: "#757575",
  }),
  indicatorSeparator: (provided) => ({ ...provided, minHeight: "1px" }),
  clearIndicator: (provided) => ({ ...provided, minHeight: "1px" }),
  valueContainer: (provided) => ({
    ...provided,
    minHeight: "1px",
    paddingTop: "0",
    paddingBottom: "0",
  }),
  singleValue: (provided) => ({
    ...provided,
    minHeight: "1px",
    paddingBottom: "2px",
  }),
};

// ======================================================================
// GENERA OPZIONI MESE/ANNO DA GEN 2024 A OGGI
// ======================================================================
export const generateMonthYearOptions = () => {
  const options = [];
  const startDate = new Date(2024, 0);
  const endDate = new Date().setMonth(new Date().getMonth() - 2);
  const currentDate = new Date(startDate);
  const formatterShort = new Intl.DateTimeFormat("it-IT", { month: "short", year: "numeric" });

  while (currentDate <= endDate) {
    const value = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const label = formatterShort.format(value).toUpperCase();
    options.push({ value, label });
    currentDate.setMonth(currentDate.getMonth() + 1);
  }
  return options.reverse();
};

// ======================================================================
// AGGIUNGE IL CAMPO "periodo" AI DATI GREZZI
// ======================================================================
export const transformDataWithPeriod = (rawData, periodo, typeDataSources) => {
  return rawData.map((item) => ({ ...item, periodo, type_data: typeDataSources.join(",") }));
};

// ======================================================================
// RAGGRUPPA DATI PER SKU, REGIONE, PROV → CREA OGGETTO "periodi"
// ======================================================================
export const pivotData = (allRows) => {
  const allPeriods = [...new Set(allRows.map((r) => r.periodo))].sort();
  const grouped = {};

  allRows.forEach((row) => {
    const key = `${row.sku}-${row.regione}-${row.prov}`;
    if (!grouped[key]) {
      grouped[key] = {
        sku: row.sku,
        regione: row.regione,
        prov: row.prov,
        linea: String(row.linea || "").toUpperCase(),
        periodi: {},
        fatturato: {},
      };
      allPeriods.forEach((p) => {
        grouped[key].periodi[p] = 0;
        grouped[key].fatturato[p] = 0;
      });
    }
    grouped[key].periodi[row.periodo] = (grouped[key].periodi[row.periodo] || 0) + (row.qty || 0);
    grouped[key].fatturato[row.periodo] = (grouped[key].fatturato[row.periodo] || 0) + (row.fatturato || 0);
  });

  return Object.values(grouped);
};

// ======================================================================
// CALCOLA DIFF, TREND E % TOTALE
// ======================================================================
export const calculateDiffAndTrend = (periodi, isAggregated = false) => {
  const sortedPeriods = Object.keys(periodi || {}).sort();

  if (sortedPeriods.length === 0) {
    return { diff: 0, trend: "neutral", percentage: 0, fatturato: 0 };
  }

  if (sortedPeriods.length === 1) {
    return { diff: 0, trend: "neutral", percentage: 0, fatturato: 0 };
  }

  const isPeriodAggregated = (period) => period.includes(" - ");
  const relevantPeriods = sortedPeriods.filter((p) =>
    isAggregated ? isPeriodAggregated(p) : !isPeriodAggregated(p),
  );

  if (relevantPeriods.length < 2) {
    return { diff: 0, trend: "neutral", percentage: 0, fatturato: 0 };
  }

  const values = relevantPeriods.map((p) => periodi[p] || 0);
  const diffs = values.slice(1).map((v, i) => v - values[i]);
  const avgDiff = diffs.reduce((a, b) => a + b, 0) / diffs.length;

  const first = values[0];
  const last = values[values.length - 1];

  let percentage = 0;
  let fatturato = 0;
  if (first === 0) {
    percentage = last > 0 ? 100 : last < 0 ? -100 : 0;
    fatturato = last > 0 ? last : -last;
  } else {
    percentage = ((last - first) / Math.abs(first)) * 100;
    fatturato = last - first;
  }

  let trend = "neutral";
  if (avgDiff > 0) trend = "positive";
  else if (avgDiff < 0) trend = "negative";

  return {
    diff: Math.round(avgDiff),
    trend,
    percentage: parseFloat(percentage.toFixed(2)),
    fatturato: parseFloat(fatturato.toFixed(2)),
  };
};

// ======================================================================
// FORMATA DATA IN YYYY-MM-DD PER CONFRONTO COLONNE
// ======================================================================
export const formatIsoDateForImsColumns = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// ======================================================================
// COSTRUISCE COLONNE DINAMICHE PER GRIGLIA
// ======================================================================
export const buildImsDynamicColumns = ({ rawRows, monthYearOptions, layoutMobile, aggregationType }) => {
  const periods = sortPeriodsChronologically([...new Set(rawRows.map((r) => r.periodo))]);
  const cols = [{ name: "productName", title: "Prodotto" }];

  if (periods.length > 1) {
    cols.push(
      { name: "diff", title: "Diff." },
      { name: "percentage", title: "%" },
      { name: "trend", title: "Trend" },
    );
  }

  if (!layoutMobile || (layoutMobile && periods.length < 2)) {
    cols.push({ name: "linea", title: "Linea" });
    periods.forEach((period) => {
      let label;
      if (period.includes(" - ")) {
        label = period.replace(/20(\d{2})/g, "$1");
      } else {
        const fullLabel =
          monthYearOptions.find((opt) => formatIsoDateForImsColumns(opt.value) === period)?.label || period;
        label = fullLabel.replace(/20(\d{2})/g, "$1");
      }
      cols.push({
        name: `qty_${period}`,
        title: label,
        getCellValue: (row) => row.periodi?.[period] ?? 0,
      });
      cols.push({
        name: `fatturato_${period}`,
        title: "Stima €€",
        getCellValue: (row) => row.fatturato?.[period] ?? 0,
      });
    });

    if (aggregationType === "line") {
      cols.push({ name: "regione", title: "REG" });
    }
  }

  return cols;
};
